import { useState } from "react";
import type { ScheduleEntry } from "@edu/shared";
import { CancelOccurrenceForm, CancelWithLessonNote } from "./CancelOccurrenceForm.tsx";
import { EndSeriesForm } from "./EndSeries.tsx";
import { EntryToolButtons } from "./EntryToolButtons.tsx";
import { MoveOccurrenceForm } from "./MoveOccurrenceForm.tsx";
import { OverrideForm } from "./OverrideForm.tsx";
import { SingleEntryTools } from "./SingleEntryTools.tsx";
import { seriesCutDateOf } from "./series-end-text.ts";
import { useOccurrenceActions } from "./use-occurrence-actions.ts";

/**
 * Отмена, перенос, правка и возврат занятия серии, своего и группового.
 *
 * Отдельным файлом от карточки с 20.08.2026: вместе они давали 770 строк при
 * потолке в 400 (раздел 9 CLAUDE.md), и правка кнопки требовала пролистать
 * форму заготовок. Граница проходит там же, где смысловая: карточка ОПИСЫВАЕТ
 * занятие, этот файл его МЕНЯЕТ, а форма особого занятия уехала третьим файлом
 * (`OverrideForm.tsx`) по той же причине и в тот же день. Разовое занятие
 * уехало четвёртым (`SingleEntryTools.tsx`) 30.08.2026: у него правится сам
 * урок, а не строка исключения, то есть общих ручек с этим файлом нет вовсе.
 *
 * ⚠️ Только преподавателю, и решает это не экран: ручки отвечают ученику 404.
 * Здесь роль проверяется затем, чтобы не рисовать кнопки, которых у человека
 * нет, а не вместо серверной проверки.
 *
 * Состоявшееся занятие не двигается никак, поэтому у `live` и `done` кнопок
 * тоже нет: сервер откажет `lesson_started`, и предлагать это действие значит
 * предлагать отказ.
 *
 * ⚠️ **Режимы разъехались по файлам 30.08.2026, и это раскладка, а не новое
 * поведение.** Функция дошла до двухсот семидесяти строк при потолке в сто
 * пятьдесят (раздел 9 CLAUDE.md), а внутри неё сидели четыре режима сразу.
 * Ряд значков уехал в `EntryToolButtons.tsx`, отмена с её второй ступенью в
 * `CancelOccurrenceForm.tsx`, перенос в `MoveOccurrenceForm.tsx`, тем же ходом,
 * каким раньше уехали `OverrideForm.tsx` и `SingleEntryTools.tsx`. Здесь
 * остаётся выбор режима и раскладка форм по нему.
 *
 * ⚠️ **Состояние форм живёт в хуке `useOccurrenceActions`**
 * (`use-occurrence-actions.ts`, вынесен 08.10.2026 вместе с ручками отмены,
 * переноса и возврата), а хук живёт столько же, сколько `EntryTools`. Причина в
 * причине отмены: набранная в форме отмены, она уезжает и с переносом и
 * переживает смену режима, а живя в форме, стиралась бы при каждом её закрытии.
 *
 * Пятый режим, `end`, это завершение серии с даты нажатого занятия
 * (`EndSeries.tsx`). Вход в него только из формы отмены, строкой «Убрать это и
 * все следующие»: человек, решивший убрать занятие, и есть тот, кому нужен
 * вопрос «а следующие тоже?». Состояние завершения живёт в самой форме, поэтому
 * каждый вход начинается с первого шага.
 *
 * ⚠️ Возвращается ФРАГМЕНТ, а не своя коробка. Ряд действий (`.wk__tools`)
 * держит карточка, потому что в том же ряду стоит главная кнопка, а её этот
 * файл не рисует. Порядок внутри ряда задан `order` в стилях: формы уезжают на
 * свою строку независимо от того, в каком порядке они пришли в разметку.
 */
export function EntryTools({
  entry,
  zone,
  busy,
  run,
}: {
  entry: ScheduleEntry;
  zone: string;
  busy: boolean;
  run: (fn: () => Promise<void>) => Promise<void>;
}) {
  const [mode, setMode] = useState<"none" | "cancel" | "move" | "custom" | "end">("none");
  /**
   * Дата завершения, снятая в момент нажатия, а не пересчитанная на каждом
   * рендере: занятие, начавшееся при открытой форме, иначе сняло бы форму вместе
   * с рядом значков. Сервер в этом случае сам откажет и назовёт дату позже.
   */
  const [endFrom, setEndFrom] = useState<string | null>(null);
  const act = useOccurrenceActions({ entry, zone, run, onDone: () => setMode("none") });

  if (entry.myRole !== "teacher") return null;
  if (entry.state === "live" || entry.state === "done") return null;
  if (entry.standalone) {
    return <SingleEntryTools entry={entry} zone={zone} busy={busy} run={run} />;
  }
  if (entry.source !== "series" || entry.slotId === null || entry.occursOn === null) return null;

  const slotId = entry.slotId;
  const occursOn = entry.occursOn;
  const seriesId = entry.seriesId;
  // Адрес занятия группы собирает хук. ⚠️ Четвёртого инструмента, «Сделать
  // особым», у группы нет, разбор в подписи `personal` у `EntryToolButtons.tsx`
  const group = entry.group;
  // Формы отмены и переноса спрашивают число, а не всю группу: оно нужно им для
  // слов о цене, а `null` означает «занятие пары, цены называть не надо»
  const students = group === undefined ? null : group.size;
  const parked = entry.state === "cancelled" || entry.state === "moved_away";
  /** Можно ли убрать это занятие и все следующие. Правило в `seriesCutDateOf`. */
  const cutDate = seriesCutDateOf(entry, Date.now());

  return (
    <>
      {mode === "none" && (
        <EntryToolButtons
          personal={group === undefined}
          customized={entry.customized}
          parked={parked}
          canEnd={cutDate !== null}
          busy={busy}
          onPick={setMode}
          onRestore={act.restore}
        />
      )}

      {mode === "cancel" && (
        <CancelOccurrenceForm
          students={students}
          busy={busy}
          reason={act.reason}
          onReason={act.setReason}
          onCancel={() => act.cancel(false)}
          onClose={() => setMode("none")}
        />
      )}

      {/*
        Второй выбор рядом с отменой, тихой кнопкой и без вопроса перед ней:
        главное действие формы это отмена одного занятия. Пока висит вторая
        ступень отмены с уроком, строки нет, иначе под одной формой стояли бы
        два подтверждения про разное
      */}
      {mode === "cancel" && cutDate !== null && !act.confirmLesson && (
        <p className="muted">
          <button
            type="button"
            className="btn btn--quiet"
            disabled={busy}
            onClick={() => {
              act.setConfirmLesson(false);
              setEndFrom(cutDate);
              setMode("end");
            }}
          >
            Убрать это и все следующие
          </button>
        </p>
      )}

      {mode === "end" && endFrom !== null && seriesId !== null && (
        <EndSeriesForm
          seriesId={seriesId}
          from={endFrom}
          reasonTyped={act.reason.trim() !== ""}
          busy={busy}
          run={run}
          onBack={() => setMode("cancel")}
          onClose={() => setMode("none")}
        />
      )}

      {mode === "move" && (
        <MoveOccurrenceForm
          students={students}
          zone={act.formZone}
          busy={busy}
          toDate={act.toDate}
          toTime={act.toTime}
          onDate={act.setToDate}
          onTime={act.setToTime}
          onMove={act.move}
          onClose={() => setMode("none")}
        />
      )}

      {mode === "custom" && (
        <OverrideForm
          entry={entry}
          busy={busy}
          run={run}
          onDone={() => setMode("none")}
          slotId={slotId}
          occursOn={occursOn}
        />
      )}

      {act.confirmLesson && (
        <CancelWithLessonNote
          busy={busy}
          onConfirm={() => act.cancel(true)}
          onClose={() => act.setConfirmLesson(false)}
        />
      )}
    </>
  );
}
